"use client";

import { QUERY_KEYS } from "@frontend/roz-sdk-core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useAuth } from "./useAuth";
import { useSdk } from "./useSdk";

export interface UseUserResult<TUser = unknown> {
  user: TUser | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export const useUser = <TUser = unknown>(): UseUserResult<TUser> => {
  const sdk = useSdk();
  const { isAuthorized } = useAuth();
  const previousSdkRef = useRef(sdk);
  const { data, isLoading, refetch } = useQuery({
    enabled: isAuthorized,
    queryKey: QUERY_KEYS.user,
    queryFn: async () => {
      const result = await sdk.user.refetchUser();

      return (result?.me ?? null) as TUser | null;
    },
  });

  useEffect(() => {
    if (previousSdkRef.current === sdk) {
      return;
    }

    previousSdkRef.current = sdk;

    if (!isAuthorized) {
      return;
    }

    refetch();
  }, [isAuthorized, sdk, refetch]);

  return {
    user: (isAuthorized ? (data ?? null) : null) as TUser | null,
    isLoading: isAuthorized ? isLoading : false,
    refetch: async () => {
      if (!isAuthorized) {
        return;
      }

      await refetch();
    },
  };
};
